import React, { ReactNode, useEffect, useState } from 'react';
import './AIAnalysisModule.scss';
import { ReportViewModel } from 'types/report';
import { formatValue } from 'presentation/components/NewTable/NTCell/ValueFormatters/ValueFormatters';

// Import components
import Modal from './components/Modal';
import FloatingScrollControls from './components/FloatingScrollControls';
import StatusDisplay from './components/StatusDisplay';
import Header from './components/Header/Header';
import InfoCard, { InfoCardInteractionType } from './components/InfoCard/InfoCard';
import SimpleChart from './components/SimpleChart/SimpleChart';
import { Trend, TrendData } from './components/Trend/Trend';
import ConceptBlock from './components/ConceptBlock/ConceptBlock';
import ChatComposer from './components/ChatComposer/ChatComposer';
import ChatMessages from './components/ChatMessages';
import Greeting from './components/Greeting';
import { formatRangeValues } from 'lib/reportUtils';
// Import types
import { TrendSchema } from './types/types';

// Import context
import { AIAnalysisProvider, useAIAnalysis } from './context/AIAnalysisContext';

// Import types from types.ts
import { ContentItem, ChatMessage } from './types/types';
import { useAuthContext } from 'context/useAuthContext';
import { useAIExtendedContext } from 'hooks/useAIExtendedContext';

// Define props interface for the AIAnalysisModule
interface AIAnalysisModuleProps {
  onClose?: () => void;
  selectedRowId?: string | null;
  selectedColumnId?: string | number | null;
  // For data passed from RightPanelContainer
  data?: {
    reportData: ReportViewModel | null;
    selectedItem?: string | null;
    selectedRowId?: string | null;
    selectedColumnId?: string | number | null;
    reversed?: boolean;
  };
  // For data passed directly from the LLM
  LLMResult?: string;
}

// Inner component that uses the context
const AIAnalysisContent: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const {
    summaryData,
    isSummaryLoading,
    summaryError,
    summaryWarning,
    showReport,
    setShowReport,
    chatHistory,
    refreshSummary,
    loadSummaryFromCache,
    sendChatMessage,
    showInfoModal,
    hideInfoModal,
    modalInfo,
    scrollContainerRef,
    scrollToTop,
    scrollToBottom,
    username,
    aiAnalysis,
    cacheDate,
    reversed,
    isUsingLLMResult,
  } = useAIAnalysis();

  const chatComposerAvailable = false;
  const [dateRange, setDateRange] = useState<string | null>(null);

  useEffect(() => {
    const hierarchialData = aiAnalysis?.data?.hierarchicalData;
    if (hierarchialData) {
      const iDate = [hierarchialData[0].periodStart, hierarchialData[hierarchialData.length - 1].periodEnd];
      const initDate = formatRangeValues(iDate[0] && iDate[1] ? `${iDate[0]} - ${iDate[1]}` : iDate[0], 'KS');
      setDateRange(initDate || '');
    }
  }, [reversed, aiAnalysis]);

  // Event handlers
  const handleCardInteraction = (type: InfoCardInteractionType, data?: any, children?: ReactNode | null) => {
    // Show info modal with card data
    showInfoModal(children, 'Recommended Actions');
  };

  const handleTrendInfoClick = (data?: TrendSchema, children?: ReactNode | null) => {
    // Show info modal with trend data.
    showInfoModal(children, data?.label || 'Trend Details');
  };

  const handleRefreshClick = () => {
    // Force refresh regardless of cache status
    loadSummaryFromCache(true);
  };

  // Function to render content based on type
  const renderContent = (item: ContentItem, index: number) => {
    switch (item.type) {
      case 'chart':
        return (
          <SimpleChart
            key={index}
            data={item.data}
            reversed={reversed}
            overrideData={aiAnalysis?.data?.aiChartMetrics}
          />
        );

      case 'infoCard':
        return (
          <InfoCard
            key={index}
            showListSymbol={item.data.showListSymbol}
            tag={item.data.tag}
            date={item.data.date}
            showInfoIcon={item.data.showInfoIcon && false}
            onInteraction={handleCardInteraction}
            message={item.data.message || ''}
            isACard={item.data.isACard}
            recommendedActions={item.data.recommendedActions}
          />
        );

      case 'trend': {
        return (
          <Trend
            key={index}
            data={item.data}
            showInfoIcon={item.data.showInfoIcon && false}
            onInfoClick={handleTrendInfoClick}
            size={item.data.size}
          />
        );
      }

      default: {
        // This ensures type exhaustiveness
        const _exhaustiveCheck: never = item;
        // Log warning in development only
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`Unknown content type: ${(_exhaustiveCheck as any).type}`);
        }
        return null;
      }
    }
  };

  // Render loading state
  if (isSummaryLoading) {
    return (
      <div className="ai-analysis-module">
        <StatusDisplay
          type="loading"
          title="Generating AI Analysis"
          message="This may take up to 30-35 seconds. Our AI is analyzing your financial data to provide meaningful insights..."
        />
      </div>
    );
  }

  // Render error state
  if (summaryError) {
    return (
      <div className="ai-analysis-module">
        <StatusDisplay
          type="error"
          title="Unable to generate analysis"
          message={summaryError.message}
          actionLabel="Try Again"
          onAction={refreshSummary}
        />
      </div>
    );
  }

  // If no data is available
  if (!summaryData) {
    return (
      <div className="ai-analysis-module">
        <StatusDisplay
          type="empty"
          title="No analysis data available"
          actionLabel="Generate Analysis"
          onAction={refreshSummary}
        />
      </div>
    );
  }

  // Handle view toggling between greeting and report
  const handleViewReport = () => {
    // Toggle the report visibility
    setShowReport(!showReport);
    // Scroll to top when switching views
    scrollToTop();
  };

  const handleBackToGreeting = () => {
    setShowReport(false);
    // Scroll to top when switching views
    scrollToTop();
  };

  // Modal info component
  const ModalInfoComponent = () => {
    if (!modalInfo) return null;

    return (
      <Modal ariaHideApp={false} isOpen={!!modalInfo} onClose={hideInfoModal} title={modalInfo.title}>
        {modalInfo.content}
      </Modal>
    );
  };

  return (
    <div className="ai-analysis-module">
      <div className="ai-analysis-module__header">
        {/* Header component with refresh and close buttons (disabled when using LLMResult) */}
        <Header
          data={summaryData?.report?.header}
          onRefresh={isUsingLLMResult ? undefined : handleRefreshClick}
          onClose={isUsingLLMResult ? undefined : onClose}
          dateRange={dateRange}
        />
      </div>

      {/* Warning banner if applicable */}
      {summaryWarning && (
        <StatusDisplay type="warning" message={summaryWarning} className="ai-analysis-module__warning-banner" />
      )}

      {/* Main content area */}
      <div className="ai-analysis-module__scroll-container-wrapper">
        <FloatingScrollControls
          scrollContainerRef={scrollContainerRef}
          onScrollToTop={scrollToTop}
          onScrollToBottom={scrollToBottom}
          scrollThreshold={200}
          chatComposerAvailable={chatComposerAvailable}
        >
          <div className="ai-analysis-module__scroll-container" ref={scrollContainerRef}>
            {/* Always show greeting at the top when available */}
            {summaryData?.greeting && (
              <div className="ai-analysis-module__greeting-container">
                <Greeting data={summaryData.greeting} onViewReport={handleViewReport} isReportVisible={showReport}>
                  {/* Pass detailed report content as children */}
                  {summaryData?.report?.conceptBlocks && (
                    <div className="ai-analysis-module__summary">
                      {/* Render all concept blocks from data */}
                      {summaryData.report.conceptBlocks.map((block, blockIndex) => (
                        <ConceptBlock
                          key={blockIndex}
                          title={block.title}
                          description={block.description}
                          contentLayout={block.contentLayout}
                          bentoConfig={block.bentoConfig}
                        >
                          {block.content &&
                            block.content.length > 0 &&
                            block.content.map((item, contentIndex) => renderContent(item, contentIndex))}
                        </ConceptBlock>
                      ))}
                    </div>
                  )}
                </Greeting>
              </div>
            )}

            {/* Chat messages container */}
            <div className="ai-analysis-module__chat-container">
              <div className="ai-analysis-module__chat-messages">
                <ChatMessages messages={chatHistory} />
              </div>
            </div>
          </div>
        </FloatingScrollControls>
      </div>

      {/* Modal info overlay */}
      <ModalInfoComponent />

      {/* Chat composer at the bottom */}
      <div className="ai-analysis-module__chat-composer">
        <div className="ai-analysis-module__chat-composer__overlay-gradient">
          {chatComposerAvailable ? (
            <ChatComposer suggestions={summaryData?.report?.chatSuggestions || []} onSubmit={sendChatMessage} />
          ) : (
            <div className="chat-composer__legal">
              <div className="ai-analysis-module__chat-composer__legal">
                <b>KAI</b> can make mistakes. Please verify the information before using it.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrapper component that provides the context
const AIAnalysisModule: React.FC<AIAnalysisModuleProps> = props => {
  // Extract props from either direct props or nested data prop
  const reportData = props.data?.reportData;
  const selectedRowId = props.data?.selectedRowId || props.selectedRowId;
  const selectedColumnId = props.data?.selectedColumnId || props.selectedColumnId;
  const closeModule = props.onClose;

  const { user } = useAuthContext();
  const extendedContext = useAIExtendedContext();
  // Get username safely, with fallback to a default value
  const username = (user?.displayName || user?.email?.split('@')[0].split('.')[0] || 'User')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <AIAnalysisProvider
      reportData={reportData}
      selectedRowId={selectedRowId}
      selectedColumnId={selectedColumnId}
      username={username}
      extendedContext={extendedContext}
      reversed={props.data?.reversed}
      LLMResult={props.LLMResult}
    >
      <div className="ai-analysis-module">
        <AIAnalysisContent onClose={props.onClose} />
      </div>
    </AIAnalysisProvider>
  );
};

export default AIAnalysisModule;
